import React, { useState } from "react";
import { loginWithEmail, registerWithEmail, resetPassword } from "../lib/auth";
import { T } from "../theme";
import { Btn, Card, Input, TapeIcon } from "../ui";

function AuthShell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: T.font, padding: 32 }}>
      <TapeIcon size={72} color="#E8D4A8" accent="#C45A2D" />
      <div style={{ fontSize: 22, fontWeight: "bold", color: T.text, letterSpacing: "0.06em", marginTop: 14 }}>8TRACK VAULT</div>
      <div style={{ fontSize: 10, color: T.muted, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 28 }}>Preserve the Sound.</div>
      <div style={{ width: "100%", maxWidth: 340 }}>{children}</div>
    </div>
  );
}

function ErrorMessage({ error }) {
  if (!error) return null;
  return <div style={{ color: "#C0392B", fontSize: 12, marginBottom: 10 }}>{error}</div>;
}

export function LoginScreen({ onSwitch, onForgot }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) return setError("Enter your email and password.");
    setLoading(true);
    try {
      await loginWithEmail(email, password);
    } catch (e) {
      setError(readableAuthError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <Card>
        <form onSubmit={submit}>
          <ErrorMessage error={error} />
          <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
          <Input label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          <Btn disabled={loading} style={{ width: "100%", padding: 12, marginTop: 4 }}>
            {loading ? "Signing In…" : "Sign In"}
          </Btn>
        </form>
      </Card>
      <div style={{ textAlign: "center", marginTop: 10 }}>
        <button onClick={onForgot} style={{ background: "none", border: "none", color: T.muted, fontFamily: T.font, fontSize: 12, cursor: "pointer" }}>Forgot Password?</button>
      </div>
      <div style={{ textAlign: "center", marginTop: 4 }}>
        <button onClick={onSwitch} style={{ background: "none", border: "none", color: T.accent, fontFamily: T.font, fontSize: 12, cursor: "pointer", fontWeight: "bold" }}>Create Account</button>
      </div>
    </AuthShell>
  );
}

export function RegisterScreen({ onSwitch }) {
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) return setError("Enter your email and password.");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    setLoading(true);
    try {
      await registerWithEmail(email, password, displayName);
    } catch (e) {
      setError(readableAuthError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <Card>
        <form onSubmit={submit}>
          <ErrorMessage error={error} />
          <Input label="Display Name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g. James" />
          <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
          <Input label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" />
          <Btn disabled={loading} style={{ width: "100%", padding: 12, marginTop: 4 }}>
            {loading ? "Creating Account…" : "Create Account"}
          </Btn>
        </form>
      </Card>
      <div style={{ textAlign: "center", marginTop: 10 }}>
        <button onClick={onSwitch} style={{ background: "none", border: "none", color: T.muted, fontFamily: T.font, fontSize: 12, cursor: "pointer" }}>Already have an account? Sign In</button>
      </div>
    </AuthShell>
  );
}

export function ForgotPasswordScreen({ onSwitch }) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email) return setError("Enter your email.");
    setLoading(true);
    try {
      await resetPassword(email);
      setSent(true);
    } catch (e) {
      setError(readableAuthError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <Card>
        {sent ? (
          <div style={{ fontSize: 13, color: T.text, textAlign: "center", padding: "10px 0" }}>
            Password reset email sent. Check your inbox.
          </div>
        ) : (
          <form onSubmit={submit}>
            <ErrorMessage error={error} />
            <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            <Btn disabled={loading} style={{ width: "100%", padding: 12, marginTop: 4 }}>
              {loading ? "Sending…" : "Send Reset Email"}
            </Btn>
          </form>
        )}
      </Card>
      <div style={{ textAlign: "center", marginTop: 10 }}>
        <button onClick={onSwitch} style={{ background: "none", border: "none", color: T.accent, fontFamily: T.font, fontSize: 12, cursor: "pointer", fontWeight: "bold" }}>← Back to Sign In</button>
      </div>
    </AuthShell>
  );
}

function readableAuthError(e) {
  const code = e?.code || "";
  if (code.includes("invalid-email")) return "That email address doesn't look right.";
  if (code.includes("email-already-in-use")) return "An account with that email already exists.";
  if (code.includes("weak-password")) return "Password must be at least 6 characters.";
  if (code.includes("user-not-found") || code.includes("wrong-password") || code.includes("invalid-credential")) return "Incorrect email or password.";
  if (code.includes("too-many-requests")) return "Too many attempts. Please try again later.";
  return "Something went wrong. Please try again.";
}
